/**
 * An EN + TR pair, for one `Localized` field.
 *
 * The admin app's chrome is English; the **content** is not. Every localized field a parent
 * will read gets both boxes side by side rather than a language switcher, because writing the
 * pair together is how they stay a pair — a tagline translated three screens later is a
 * tagline that never gets translated.
 *
 * An optional field with both boxes empty becomes `undefined`, which `writes.ts` turns into a
 * real field delete. A **required** one keeps `{ en: '' }` so `validateMedium` can say which
 * field is empty, instead of `normalizeMedium` refusing the whole document.
 */

import { Field, TextAreaField } from '@atlas-nook/design-system';
import type { Localized } from '@atlas-nook/mediums';
import type { JSX } from 'react';

import { ADMIN_STRINGS as S } from '../strings.js';

export interface LocalizedFieldProps {
  label: string;
  help?: string;
  value: Localized | undefined;
  onChange: (next: Localized | undefined) => void;
  /** Keeps `{ en: '' }` rather than dropping to undefined when both boxes are empty. */
  required?: boolean;
  /** Textareas for the long ones: description, identity notes, generation notes. */
  multiline?: boolean;
  rows?: number;
}

export function nextLocalized(en: string, tr: string, required: boolean): Localized | undefined {
  const trimmedTr = tr.trim();
  if (en.trim() === '' && trimmedTr === '' && !required) return undefined;
  return trimmedTr === '' ? { en } : { en, tr };
}

export function LocalizedField({
  label,
  help,
  value,
  onChange,
  required = false,
  multiline = false,
  rows = 3,
}: LocalizedFieldProps): JSX.Element {
  const en = value?.en ?? '';
  const tr = value?.tr ?? '';
  const Control = multiline ? TextAreaField : Field;

  return (
    <div className="lpair">
      <Control
        label={`${label} · ${S.common.en}`}
        {...(help === undefined ? {} : { help })}
        {...(multiline ? { rows } : {})}
        value={en}
        onChange={(next: string) => onChange(nextLocalized(next, tr, required))}
      />
      <Control
        label={`${label} · ${S.common.tr}`}
        {...(multiline ? { rows } : {})}
        value={tr}
        onChange={(next: string) => onChange(nextLocalized(en, next, required))}
      />
    </div>
  );
}
